import React, { useCallback, useMemo, useRef, useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Skeleton,
  Chip,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  InputAdornment,
} from "@mui/material";
import ViewListOutlinedIcon from "@mui/icons-material/ViewListOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import HomeInsights, { MeetingBucket } from "./dashboard/HomeInsights";
import MeetingsCalendar, { CalendarViewMode } from "./dashboard/MeetingsCalendar";
import AddToProspoDialog from "./dashboard/AddToProspoDialog";
import OnboardClientDialog from "./dashboard/OnboardClientDialog";
import PendingClientsBanner from "./dashboard/PendingClientsBanner";
import { useCalendarEvents } from "../hooks/useCalendarEvents";
import { useClientChangeListener } from "../hooks/useClientChangeListener";
import { useAddEventToProspo } from "../hooks/useAddEventToProspo";
import { useAlert } from "../functions/useAlert";
import {
  EditOutlined as EditIcon,
  DeleteOutlined as DeleteIcon,
  Phone as PhoneIcon,
  MicOutlined as MicrophoneIcon,
} from "@mui/icons-material";
import {
  DataGrid,
  GridColDef,
  GridFooterContainer,
  GridPagination,
  useGridApiContext,
  useGridSelector,
  gridPaginationModelSelector,
  gridRowCountSelector,
} from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import isoWeek from "dayjs/plugin/isoWeek";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import teamsIcon from "../assets/images/teams.svg";
import zoomIcon from "../assets/images/zoom.svg";
import calendlyIcon from "../assets/images/calendly.svg";
import EmptyState from "./common/EmptyState";
import TableSkeleton from "./common/TableSkeleton";

dayjs.extend(isBetween);
dayjs.extend(isoWeek);
dayjs.extend(utc);
dayjs.extend(timezone);

// The panel is sized by its parent column, so these are preferences rather than
// fixed sizes: the chart is the only shrinkable child, so it gives up height
// first (down to CHART_MIN_HEIGHT) and the table keeps at least
// TABLE_MIN_HEIGHT — roughly header + footer + two rows.
const CHART_HEIGHT = 220;
const CHART_MIN_HEIGHT = 140;
const TABLE_MIN_HEIGHT = 200;
// The chart's caption line, and the panel's padding + heading — everything in
// the panel that is neither the chart nor the table.
const CAPTION_HEIGHT = 37;
const PANEL_CHROME_HEIGHT = 79;

/**
 * Granularity of the list-view chart. Defined here rather than in Homepage so
 * Homepage can import it without a cycle (Homepage already imports this file).
 *
 * "fy" is the Australian financial year (1 Jul – 30 Jun) — the period advisors
 * actually report against, which no calendar-year view can express.
 */
export type ListGranularity = "day" | "week" | "month" | "year" | "fy";

interface Meeting {
  _id: string;
  clients: { _id: string; name: string }[];
  status: "Completed" | "Upcoming" | "Today" | "This Week";
  start: { date: string; time: string };
  end: { date: string; time: string };
  utcStartTime?: string;
  utcEndTime?: string;
  timeZone?: string;
  meetingType: string;
  meetingMedium: string;
  meetingLink?: string;
  syncSource?: 'manual' | 'calendar_sync' | 'zoom_sync';
}

export interface MeetingsPanelProps {
  /** Rows for the list view — already narrowed by the range and chart-bar filters. */
  meetings: any[];
  /**
   * Every meeting in scope, unfiltered. The calendar filters by its own visible
   * period instead of the list's filters, so it needs the full set.
   */
  allMeetings?: any[];
  clients: any[];
  isMeetingsLoading: boolean;
  /** Kept for backwards compat but filtering is done upstream; pass { status:"All", meetingType:"All" } */
  filters: { status: string; meetingType: string };
  setFilters: React.Dispatch<React.SetStateAction<{ status: string; meetingType: string }>>;
  viewMode: "my" | "all";
  setViewMode: (v: "my" | "all") => void;
  meetingBuckets?: MeetingBucket[];
  selectedBucketKey?: string | null;
  setSelectedBucketKey?: (key: string | null) => void;
  /**
   * List-view chart granularity (#2080). The calendar has its own Day/Week/Month
   * toolbar inside MeetingsCalendar; this is the list's equivalent and adds Year,
   * which a calendar grid has no way to render.
   */
  listGranularity?: ListGranularity;
  onListGranularityChange?: (granularity: ListGranularity) => void;
  granularityOptions?: { value: ListGranularity; label: string }[];
  /** e.g. "August 2026" — the period the chart is currently showing. */
  rangeLabel?: string;
  /** -1 = previous period, +1 = next. */
  onListPeriodStep?: (direction: -1 | 1) => void;
  onListPeriodToday?: () => void;
  onScheduleMeeting: () => void;
  onEditMeeting: (meeting: any) => void;
  onDeleteMeeting: (meeting: any) => void;
  /** Refetch meetings after a calendar event is promoted into Prospo. */
  onMeetingAdded?: () => void;
}

const formatMeetingTime = (meeting: any) => {
  const formatString = "DD MMM, YYYY | hh:mm A";
  if (meeting.utcStartTime) {
    return dayjs(meeting.utcStartTime).format(formatString);
  }
  const oldTimeString = `${meeting.start.date}T${meeting.start.time}`;
  return dayjs.tz(oldTimeString, meeting.timeZone || "Asia/Kolkata").local().format(formatString);
};

/** The moment a meeting starts, in local time, whatever shape the record is in. */
const meetingStart = (meeting: any) =>
  meeting?.utcStartTime
    ? dayjs(meeting.utcStartTime)
    : dayjs.tz(
        `${meeting?.start?.date}T${meeting?.start?.time}`,
        meeting?.timeZone || "Asia/Kolkata",
      ).local();

/**
 * How near a meeting is. Drives row shading, which replaces the old Status
 * column (#2052) — the colour carries the "when" so the text doesn't have to
 * repeat "This Week" on every single row.
 */
type Proximity = "past" | "today" | "thisWeek" | "later";

const getProximity = (meeting: any, now = dayjs()): Proximity => {
  const start = meetingStart(meeting);
  if (!start.isValid()) return "later";
  if (start.isBefore(now, "day")) return "past";
  if (start.isSame(now, "day")) return "today";
  // Rest of the current ISO week — "this week" means the days still ahead of you,
  // not a rolling 7 days, which is how people read their own calendar.
  if (start.isBefore(now.endOf("isoWeek"))) return "thisWeek";
  return "later";
};

/**
 * Day label optimised for recognition rather than precision (#2052).
 * "19 Jun" makes you do arithmetic; "Thu 19 Jun" and "Today" don't — people
 * remember meetings by weekday, so lead with that.
 */
const formatDayLabel = (meeting: any, now = dayjs()): string => {
  const start = meetingStart(meeting);
  if (!start.isValid()) return "—";
  if (start.isSame(now, "day")) return "Today";
  if (start.isSame(now.add(1, "day"), "day")) return "Tomorrow";
  if (start.isSame(now.subtract(1, "day"), "day")) return "Yesterday";
  // Include the year only when it isn't the current one — noise otherwise.
  return start.isSame(now, "year")
    ? start.format("ddd D MMM")
    : start.format("ddd D MMM YYYY");
};

const formatTimeLabel = (meeting: any): string => {
  const start = meetingStart(meeting);
  return start.isValid() ? start.format("hh:mm A") : "";
};

const sortMeetings = (meetings: Meeting[]) => {
  const statusPriority: Record<string, number> = {
    Today: 1,
    "This Week": 2,
    Upcoming: 3,
    Completed: 4,
  };

  const getMeetingTime = (meeting: Meeting): number => {
    if (meeting.utcStartTime) return new Date(meeting.utcStartTime).getTime();
    if (meeting.start?.date && meeting.start?.time)
      return new Date(`${meeting.start.date}T${meeting.start.time}`).getTime();
    return 0;
  };

  return [...meetings].sort((a, b) => {
    const aStatus = a.status || "Upcoming";
    const bStatus = b.status || "Upcoming";
    const aPriority = statusPriority[aStatus] ?? 3;
    const bPriority = statusPriority[bStatus] ?? 3;
    if (aPriority !== bPriority) return aPriority - bPriority;
    const timeA = getMeetingTime(a);
    const timeB = getMeetingTime(b);
    return aStatus === "Completed" ? timeB - timeA : timeA - timeB;
  });
};

const MeetingsTableFooter = () => {
  const apiRef = useGridApiContext();
  const paginationModel = useGridSelector(apiRef, gridPaginationModelSelector);
  const rowCount = useGridSelector(apiRef, gridRowCountSelector);
  const from = rowCount === 0 ? 0 : paginationModel.page * paginationModel.pageSize + 1;
  const to = Math.min((paginationModel.page + 1) * paginationModel.pageSize, rowCount);
  return (
    <GridFooterContainer
      sx={{
        px: 2,
        borderTop: "1px solid #e5e7eb",
        backgroundColor: "rgba(249,250,251,0.5)",
        "& .MuiTablePagination-selectLabel": { mb: 0 },
        "& .MuiTablePagination-displayedRows": { mb: 0 },
        "& .MuiTablePagination-select": { paddingTop: 0, paddingBottom: 0, lineHeight: "1.5" },
      }}
    >
      <Typography sx={{ color: "#6b7280", alignSelf: "center" }}>
        Showing <strong style={{ color: "#111827" }}>{from}</strong> to{" "}
        <strong style={{ color: "#111827" }}>{to}</strong> of{" "}
        <strong style={{ color: "#111827" }}>{rowCount}</strong> results
      </Typography>
      <GridPagination />
    </GridFooterContainer>
  );
};

const MeetingsPanel: React.FC<MeetingsPanelProps> = ({
  meetings,
  allMeetings,
  clients,
  isMeetingsLoading,
  filters,
  meetingBuckets,
  selectedBucketKey,
  setSelectedBucketKey,
  listGranularity = "year",
  onListGranularityChange,
  granularityOptions,
  rangeLabel,
  onListPeriodStep,
  onListPeriodToday,
  onScheduleMeeting,
  onEditMeeting,
  onDeleteMeeting,
  onMeetingAdded,
}) => {
  const navigate = useNavigate();
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [meetingSearch, setMeetingSearch] = useState("");
  const userOverrideRef = useRef(false);
  const lastAutoPageSizeRef = useRef<number | null>(null);

  // Home opens on the calendar: the common question on landing is "what does my
  // week look like", which the grid answers directly and the list only implies.
  const [displayMode, setDisplayMode] = useState<"list" | "calendar">("calendar");
  const [calendarView, setCalendarView] = useState<CalendarViewMode>("week");
  const [calendarDate, setCalendarDate] = useState<Date>(new Date());
  const isCalendar = displayMode === "calendar";

  // The grid's visible span, which is wider than the period itself — a month view
  // shows trailing and leading days from the neighbouring months.
  //
  // isoWeek, not week: the grid and its label are Monday-based
  // (startOfWeekMonday in MeetingsCalendar), while plain dayjs startOf("week")
  // is Sunday-based. With the two disagreeing, RBC navigated by ITS weeks and
  // fed the result back through this Sunday-based range, so the fetched window
  // oscillated between two adjacent weeks on every render pass — visible in the
  // API logs as alternating 05-12 Sep / 12-19 Sep requests seconds apart. Each
  // one overwrote the other's results, so meetings painted and were then
  // replaced by a week that did not contain them: the "flashes but doesn't
  // stay" bug. The fetch window must use the same week definition as the grid.
  const { calendarRangeStart, calendarRangeEnd } = useMemo(() => {
    const d = dayjs(calendarDate);
    if (calendarView === "day") {
      return { calendarRangeStart: d.startOf("day"), calendarRangeEnd: d.endOf("day") };
    }
    if (calendarView === "week") {
      return { calendarRangeStart: d.startOf("isoWeek"), calendarRangeEnd: d.endOf("isoWeek") };
    }
    return {
      calendarRangeStart: d.startOf("month").startOf("isoWeek"),
      calendarRangeEnd: d.endOf("month").endOf("isoWeek"),
    };
  }, [calendarDate, calendarView]);

  // Calendar shows every meeting in the visible period. The chart-bar and range
  // filters narrow the list view only — on a calendar the period is the filter,
  // so honouring them too would blank out days the advisor navigated to.
  const meetingsForCalendar = useMemo(() => {
    const source = allMeetings && allMeetings.length ? allMeetings : meetings;
    return (source || []).filter((m: any) =>
      meetingStart(m).isBetween(calendarRangeStart, calendarRangeEnd, null, "[]"),
    );
  }, [allMeetings, meetings, calendarRangeStart, calendarRangeEnd]);

  const [setAlert] = useAlert();

  const {
    events: calendarEvents,
    loading: calendarLoading,
    failed: calendarFailed,
    refresh: refreshCalendarEvents,
  } = useCalendarEvents(
    meetingsForCalendar,
    calendarRangeStart,
    calendarRangeEnd,
    // Not gated on isCalendar: the pending-clients banner renders in both
    // views, and gating this meant list view never fetched — so the banner
    // had no data and silently rendered nothing there.
    true,
    // 1-1s with known clients are filed automatically — say so, since records
    // appearing without a click is otherwise disconcerting.
    (imported) => {
      setAlert(
        imported.length === 1
          ? `"${imported[0].title}" added to ${imported[0].clientName}.`
          : `${imported.length} meetings added to Prospo.`,
        "success",
      );
      onMeetingAdded?.();
    },
  );

  const {
    pendingEvent,
    submitting,
    addingId,
    needsOnboarding,
    requestAdd,
    cancel: cancelAdd,
    confirmAdd,
    confirmOnboard,
  } = useAddEventToProspo(() => {
    refreshCalendarEvents();
    onMeetingAdded?.();
  });

  // A client created anywhere invalidates the banner's classification, including
  // from a screen with no knowledge of this calendar (the Clients page). Without
  // this the count only corrected on a full page reload.
  useClientChangeListener(useCallback(() => {
    refreshCalendarEvents();
  }, [refreshCalendarEvents]));

  /** Yellow rows — 1-1s waiting on a client, surfaced together in the banner. */
  const pendingClientEvents = useMemo(
    // Includes Calendly bookings that are already Prospo meetings but have no
    // client — they need the same "create the client" action.
    () => calendarEvents.filter((e) => e.shape === "oneToOneUnknown"),
    [calendarEvents],
  );

  useEffect(() => {
    const el = tableWrapperRef.current;
    if (!el) return;

    const computePageSize = () => {
      const availableHeight = el.clientHeight;
      const rows = Math.floor((availableHeight - 56 - 52) / 48); // header + footer overhead
      const options = [5, 10, 20, 30, 40, 50];
      const snapped = options.reduce((prev, curr) =>
        Math.abs(curr - rows) < Math.abs(prev - rows) ? curr : prev,
      );
      const newPageSize = Math.max(5, snapped);

      // Zoom changed enough to land on a different option — clear the user override
      if (newPageSize !== lastAutoPageSizeRef.current) {
        lastAutoPageSizeRef.current = newPageSize;
        userOverrideRef.current = false;
      }

      if (userOverrideRef.current) return;

      setPaginationModel((prev) => {
        if (prev.pageSize === newPageSize) return prev;
        const firstVisibleIndex = prev.page * prev.pageSize;
        return { page: Math.floor(firstVisibleIndex / newPageSize), pageSize: newPageSize };
      });
    };

    const observer = new ResizeObserver(computePageSize);
    observer.observe(el);
    computePageSize();
    return () => observer.disconnect();
  }, []);

  const filteredMeetings = useMemo(() => {
    const now = dayjs();
    const startOfWeek = now.startOf("isoWeek");
    const endOfWeek = now.endOf("isoWeek");

    const getSafeMeetingStart = (meeting: Meeting) => {
      if (meeting.utcStartTime) return dayjs(meeting.utcStartTime);
      return dayjs.tz(`${meeting.start.date}T${meeting.start.time}`, meeting.timeZone || "Asia/Kolkata");
    };

    const getSafeMeetingEnd = (meeting: Meeting) => {
      if (meeting.utcEndTime) return dayjs(meeting.utcEndTime);
      return dayjs.tz(`${meeting.end.date}T${meeting.end.time}`, meeting.timeZone || "Asia/Kolkata");
    };

    const filtered = meetings.filter((meeting) => {
      const meetingStart = getSafeMeetingStart(meeting);
      const meetingEnd = getSafeMeetingEnd(meeting);

      const typeMatch =
        filters.meetingType === "All" || meeting.meetingType === filters.meetingType;
      if (!typeMatch) return false;

      switch (filters.status) {
        case "All": return true;
        case "Upcoming": return meetingStart.isAfter(now);
        case "Today": return meetingStart.isSame(now, "day");
        case "This Week": return meetingStart.isBetween(startOfWeek, endOfWeek, null, "[]");
        case "Completed": return meetingEnd.isBefore(now);
        default: return true;
      }
    });

    return sortMeetings(filtered);
  }, [meetings, filters]);

  const meetingRows = useMemo(() => {
    const query = meetingSearch.trim().toLocaleLowerCase();

    return filteredMeetings
      .map((m: any) => {
        const client = clients.find((c) => c._id === m.clientid);
        return {
          id: m._id,
          _id: m._id,
          clientName: client ? client.name : "N/A",
          status: m.status,
          startAt: formatMeetingTime({ utcStartTime: m.utcStartTime, start: m.start }),
          // Drives row shading + the Date column (#2052)
          proximity: getProximity(m),
          dayLabel: formatDayLabel(m),
          timeLabel: formatTimeLabel(m),
          meetingMedium: m.meetingMedium,
          meetingLink: m.meetingLink,
          advisors: m.advisors,
          ...m,
        };
      })
      .filter((meeting: any) =>
        !query ||
        meeting.clientName?.toLocaleLowerCase().includes(query) ||
        meeting.title?.toLocaleLowerCase().includes(query),
      );
  }, [filteredMeetings, clients, meetingSearch]);

  useEffect(() => {
    setPaginationModel((current) => ({ ...current, page: 0 }));
  }, [meetingSearch]);

  const meetingColumns: GridColDef[] = [
    {
      field: "clientName",
      headerName: "Client",
      minWidth: 120,
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography sx={{ fontWeight: 500, color: "#111827", fontSize: "0.875rem" }}>
            {row.clientName}
          </Typography>
        </Box>
      ),
    },
    {
      field: "title",
      headerName: "Meeting Title",
      minWidth: 140,
      flex: 1.2,
      align: "left",
      headerAlign: "left",
      renderCell: ({ row }) => {
        const isSynced = row.syncSource && row.syncSource !== "manual";
        const syncLabel = row.syncSource === "zoom_sync" ? "Zoom" : "Outlook";
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, height: "100%", minWidth: 0 }}>
            <Typography
              sx={{
                color: "#4b5563",
                fontSize: "0.875rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {row.title}
            </Typography>
            {isSynced && (
              <Chip
                label={`Synced · ${syncLabel}`}
                size="small"
                sx={{
                  height: 18,
                  fontSize: "0.65rem",
                  fontWeight: 600,
                  bgcolor: "#eff6ff",
                  color: "#1d4ed8",
                  "& .MuiChip-label": { px: 0.75 },
                }}
              />
            )}
          </Box>
        );
      },
    },
    {
      field: "category",
      headerName: "Type",
      minWidth: 100,
      flex: 0.7,
      align: "left",
      headerAlign: "left",
      renderCell: ({ row }) => {
        const isGeneral = row.category === "general";
        const badge = isGeneral
          ? { bg: "#f5f3ff", color: "#6d28d9", border: "1px solid #ddd6fe", label: "General" }
          // Display label only — the stored category value stays "financial".
          : { bg: "#e0f2fe", color: "#0369a1", border: "1px solid #bae6fd", label: "Advice" };
        return (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", height: "100%" }}>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                px: "8px",
                height: "22px",
                borderRadius: "4px",
                backgroundColor: badge.bg,
                border: badge.border,
                fontSize: "0.8rem",
                fontWeight: 500,
                color: badge.color,
                whiteSpace: "nowrap",
              }}
            >
              {badge.label}
            </Box>
          </Box>
        );
      },
    },
    {
      field: "startAt",
      headerName: "When",
      minWidth: 130,
      flex: 0.9,
      align: "left",
      headerAlign: "left",
      // Weekday first, and "Today"/"Tomorrow" where that says more than a date
      // (#2052). The full date stays available on hover.
      renderCell: ({ row }) => {
        const isNear = row.proximity === "today" || row.proximity === "thisWeek";
        return (
          <Tooltip title={row.startAt || ""} arrow>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", height: "100%" }}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography
                  sx={{
                    fontSize: "0.875rem",
                    fontWeight: isNear ? 700 : 500,
                    color: row.proximity === "past" ? "#6b7280" : "#111827",
                    lineHeight: 1.4,
                  }}
                >
                  {row.dayLabel || "—"}
                </Typography>
                <Typography sx={{ fontSize: "0.8125rem", color: "#6b7280", mt: "2px", lineHeight: 1.4 }}>
                  {row.timeLabel || ""}
                </Typography>
              </Box>
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "meetingMedium",
      headerName: "Medium",
      minWidth: 120,
      flex: 0.8,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => {
        const medium = row.meetingMedium || "PC / Microphone";
        let icon;
        if (medium === "Teams Meeting") {
          icon = <img src={teamsIcon} width="18px" alt="Teams Meeting" />;
        } else if (medium === "Zoom") {
          icon = <img src={zoomIcon} width="18px" alt="Zoom" />;
        } else if (medium === "Calendly") {
          icon = <img src={calendlyIcon} width="18px" alt="Calendly" />;
        } else if (medium === "Phone Call") {
          icon = <PhoneIcon sx={{ fontSize: 18, color: "#9ca3af" }} />;
        } else {
          icon = <MicrophoneIcon sx={{ fontSize: 18, color: "#9ca3af" }} />;
        }

        if (row.meetingLink) {
          return (
            <Box
              sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}
            >
              <Box
                component="button"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(row.meetingLink, "_blank");
                }}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  px: "12px",
                  py: "6px",
                  borderRadius: "20px",
                  backgroundColor: "#eff6ff",
                  color: "#2563eb",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                  "&:hover": { backgroundColor: "#dbeafe" },
                }}
              >
                {icon}
                Join Call
              </Box>
            </Box>
          );
        }
        return (
          <Box
            sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}
          >
            <Tooltip title={medium}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  backgroundColor: "#e5e7eb",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {icon}
              </Box>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 80,
      flex: 0.6,
      align: "center",
      headerAlign: "center",
      sortable: false,
      cellClassName: "sticky-actions-column",
      renderCell: ({ row }) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            gap: "4px",
            opacity: 0.8,
            "&:hover": { opacity: 1 },
          }}
        >
          <Tooltip title="Edit Meeting">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onEditMeeting(row);
              }}
              sx={{
                color: "#2563eb",
                borderRadius: "6px",
                p: "6px",
                "&:hover": { backgroundColor: "#eff6ff", color: "#1d4ed8" },
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Meeting">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDeleteMeeting(row);
              }}
              sx={{
                color: "#ef4444",
                borderRadius: "6px",
                p: "6px",
                "&:hover": { backgroundColor: "#fef2f2", color: "#b91c1c" },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const hasChart = isMeetingsLoading || (meetingBuckets?.length ?? 0) > 0;

  return (
    <Box sx={{ display: "flex", flexGrow: 1, minHeight: 0, width: "100%" }}>
      <Paper
        id="meetings-panel"
        elevation={2}
        sx={{
          p: 2,
          borderRadius: "12px",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          width: "100%",
          // No fixed height: the panel takes whatever the dashboard column has
          // left. The chart shrinks first, then the min heights force the page
          // to scroll rather than collapsing the table to nothing.
          minHeight: {
            xs: `${(hasChart ? CHART_HEIGHT + CAPTION_HEIGHT : 0) + TABLE_MIN_HEIGHT + PANEL_CHROME_HEIGHT}px`,
            lg: `${(hasChart ? CHART_MIN_HEIGHT + CAPTION_HEIGHT : 0) + TABLE_MIN_HEIGHT + PANEL_CHROME_HEIGHT}px`,
          },
        }}
      >
        <Box
          sx={{
            mb: 2,
            // flexShrink guards the heading against the panel's shrink-the-chart
            // sizing; the row layout puts the List/Calendar toggle beside it.
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="h6" fontWeight="bold">Meetings</Typography>

          <ToggleButtonGroup
            id="meetings-display-toggle"
            value={displayMode}
            exclusive
            size="small"
            onChange={(_, next) => next && setDisplayMode(next)}
            sx={{
              backgroundColor: "#f3f4f6",
              borderRadius: "8px",
              "& .MuiToggleButton-root": {
                textTransform: "none",
                px: 1.5,
                py: 0.25,
                fontSize: "0.8rem",
                border: "1px solid #e5e7eb",
                color: "#374151",
              },
              "& .Mui-selected": {
                backgroundColor: "#fff !important",
                color: "#111827",
                fontWeight: 600,
              },
            }}
          >
            <ToggleButton value="calendar">
              <CalendarMonthOutlinedIcon sx={{ fontSize: "1rem", mr: 0.5 }} /> Calendar
            </ToggleButton>
            <ToggleButton value="list">
              <ViewListOutlinedIcon sx={{ fontSize: "1rem", mr: 0.5 }} /> List
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Above the view switch: the blocked 1-1s are the same in either view. */}
        <PendingClientsBanner events={pendingClientEvents} onAdd={requestAdd} />

        {isCalendar ? (
          <Box
            sx={{
              flexGrow: 1,
              width: "100%",
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              "& > :last-child": { flexGrow: 1, minHeight: 0, display: "flex", flexDirection: "column" },
              "& > :last-child > :last-child": { flexGrow: 1, minHeight: 0 },
            }}
          >
            {calendarFailed && (
              <Alert severity="info" sx={{ mb: 1.5 }}>
                Showing Prospo meetings only — your calendar could not be reached.
              </Alert>
            )}
            <MeetingsCalendar
              events={calendarEvents}
              loading={calendarLoading}
              view={calendarView}
              onViewChange={setCalendarView}
              date={calendarDate}
              onDateChange={setCalendarDate}
              onSelectMeeting={(m) => navigate(`/meetings/${m._id}`, { state: { meeting: m } })}
              onAddToProspo={requestAdd}
              addingId={addingId}
            />
          </Box>
        ) : (
        <>
        {/* Period navigation + granularity for the chart below. Mirrors the
            calendar's own toolbar, plus Year — which a calendar grid can't show. */}
        {granularityOptions && granularityOptions.length > 0 && (
          <Box
            sx={{
              mb: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <IconButton
                size="small"
                aria-label="Previous period"
                onClick={() => onListPeriodStep?.(-1)}
                sx={{ border: "1px solid #e5e7eb", borderRadius: "8px", p: "4px" }}
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
              <Box
                component="button"
                onClick={() => onListPeriodToday?.()}
                sx={{
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  backgroundColor: "#fff",
                  cursor: "pointer",
                  px: 1.25,
                  py: "5px",
                  fontSize: "0.8rem",
                  color: "#374151",
                  "&:hover": { backgroundColor: "#f9fafb" },
                }}
              >
                Today
              </Box>
              <IconButton
                size="small"
                aria-label="Next period"
                onClick={() => onListPeriodStep?.(1)}
                sx={{ border: "1px solid #e5e7eb", borderRadius: "8px", p: "4px" }}
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>
              {rangeLabel && (
                <Typography sx={{ ml: 1, fontWeight: 600, color: "#111827", fontSize: "0.95rem" }}>
                  {rangeLabel}
                </Typography>
              )}
            </Box>

            <ToggleButtonGroup
              value={listGranularity}
              exclusive
              size="small"
              onChange={(_, next) => next && onListGranularityChange?.(next)}
              sx={{
                backgroundColor: "#f3f4f6",
                borderRadius: "8px",
                "& .MuiToggleButton-root": {
                  textTransform: "none",
                  px: 1.5,
                  py: 0.25,
                  fontSize: "0.8rem",
                  border: "1px solid #e5e7eb",
                  color: "#374151",
                },
                "& .Mui-selected": {
                  backgroundColor: "#fff !important",
                  color: "#111827",
                  fontWeight: 600,
                },
              }}
            >
              {granularityOptions.map((g) => (
                <ToggleButton key={g.value} value={g.value}>
                  {g.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        )}

        {/* Chart — shown when data is loaded and available */}
        {isMeetingsLoading ? (
          <Box sx={{ mb: 1.5, flexShrink: 1, minHeight: 0, overflow: "hidden" }}>
            <Skeleton variant="rounded" animation="wave" height={CHART_HEIGHT} sx={{ borderRadius: "12px" }} />
            <Skeleton variant="text" animation="wave" width={220} sx={{ mt: 0.5 }} />
          </Box>
        ) : meetingBuckets && meetingBuckets.length > 0 ? (
          <>
            <Box
              sx={{
                height: CHART_HEIGHT,
                minHeight: CHART_MIN_HEIGHT,
                flexShrink: 1,
              }}
            >
              <HomeInsights
                buckets={meetingBuckets}
                selectedKey={selectedBucketKey ?? null}
                onSelect={(key) =>
                  setSelectedBucketKey && setSelectedBucketKey(selectedBucketKey === key ? null : key)
                }
              />
            </Box>
          </>
        ) : null}

        {!isMeetingsLoading && meetings.length > 0 && (
          <TextField
            value={meetingSearch}
            onChange={(event) => setMeetingSearch(event.target.value)}
            placeholder="Search by client or meeting title"
            aria-label="Search meetings by client or meeting title"
            size="small"
            fullWidth
            sx={{ mt: hasChart ? 0.5 : 0, mb: 1.5, flexShrink: 0 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon sx={{ color: "#6b7280", fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
        )}

        {/* Key for the row bands (#2052). A colour code needs one, but it earns
            its place only by staying tiny — two swatches, two words. */}
        {!isMeetingsLoading && meetings.length > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1, pl: 0.5 }}>
            {[
              { label: "Today", bg: "#fff8ed", bar: "#f59e0b" },
              { label: "This week", bg: "#f5f9ff", bar: "#93c5fd" },
            ].map((k) => (
              <Box key={k.label} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Box
                  sx={{
                    width: 18,
                    height: 12,
                    borderRadius: "3px",
                    backgroundColor: k.bg,
                    boxShadow: `inset 3px 0 0 ${k.bar}`,
                    border: "1px solid #e5e7eb",
                  }}
                />
                <Typography sx={{ fontSize: "0.75rem", color: "#6b7280" }}>{k.label}</Typography>
              </Box>
            ))}
          </Box>
        )}

        <Box
          ref={tableWrapperRef}
          sx={{
            // Basis 0 so the table never claims the DataGrid's intrinsic
            // height; it takes the leftover space and stops at TABLE_MIN_HEIGHT,
            // which pushes the shrinking onto the chart above it.
            flex: "1 0 0px",
            minHeight: `${TABLE_MIN_HEIGHT}px`,
            width: "100%",
            overflow: "hidden",
          }}
        >
          {isMeetingsLoading ? (
            <TableSkeleton rows={8} cols={6} rowHeight={48} />
          ) : meetings.length === 0 ? (
            <EmptyState
              icon={<EventOutlinedIcon sx={{ fontSize: 48 }} />}
              title="No meetings yet"
              description="Schedule your first meeting to start tracking client interactions."
              primaryAction={{
                label: "+ Schedule a Meeting",
                onClick: onScheduleMeeting,
              }}
            />
          ) : (
            <DataGrid
              rows={meetingRows}
              columns={meetingColumns}
              paginationModel={paginationModel}
              onPaginationModelChange={(model) => {
                userOverrideRef.current = true;
                setPaginationModel(model);
              }}
              pageSizeOptions={[5, 10, 20, 30, 40, 50]}
              disableRowSelectionOnClick
              rowHeight={48}
              // Proximity is carried by colour instead of a Status column (#2052):
              // today is the warm band, the rest of this week is the cool band,
              // everything else is plain. One glance, no reading.
              getRowClassName={(params) => `prospo-row--${params.row.proximity || "later"}`}
              slots={{ footer: MeetingsTableFooter }}
              onRowClick={(params) =>
                navigate(`/meetings/${params.row._id}`, { state: { meeting: params.row } })
              }
              sx={{
                "& .MuiDataGrid-row": { cursor: "pointer" },
                border: "none",
                overflow: "hidden",
                width: "100%",
                height: "100%",
                "& .MuiDataGrid-virtualScroller": { overflowY: "auto" },
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "rgba(249, 250, 251, 0.8)",
                  borderBottom: "1px solid #e5e7eb",
                  "& .MuiDataGrid-columnHeader": { px: "16px", py: "12px" },
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  textTransform: "uppercase",
                  fontSize: "0.7rem",
                  letterSpacing: "0.05em",
                  fontWeight: 600,
                  color: "#6b7280",
                },
                "& .MuiDataGrid-cell": { px: "16px", py: "12px" },
                "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": { outline: "none" },
                "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
                  outline: "none",
                },
                // ── Proximity bands (#2052) ──────────────────────────────
                // Deliberately low-saturation: these tint whole rows, so anything
                // stronger would fight the status/type chips inside them.
                "& .prospo-row--today": {
                  backgroundColor: "#fff8ed",
                  boxShadow: "inset 3px 0 0 #f59e0b",
                  "&:hover": { backgroundColor: "#fef3e2" },
                },
                "& .prospo-row--thisWeek": {
                  backgroundColor: "#f5f9ff",
                  boxShadow: "inset 3px 0 0 #93c5fd",
                  "&:hover": { backgroundColor: "#eef5ff" },
                },
                // Past meetings recede rather than getting a band of their own —
                // they're the default backdrop, not a thing to notice.
                "& .prospo-row--past": { backgroundColor: "transparent", opacity: 0.72 },
                "& .MuiDataGrid-row:hover": { backgroundColor: "rgba(249, 250, 251, 0.8)" },
                "& .MuiDataGrid-footerContainer": { overflow: "hidden" },
                "& .MuiTablePagination-displayedRows": { display: "none" },
              }}
            />
          )}
        </Box>
        </>
        )}

        {/* A 1-1 with someone new needs the contact and client created first;
            everything else just needs a client picked. */}
        <OnboardClientDialog
          open={!!pendingEvent && needsOnboarding}
          event={pendingEvent}
          submitting={submitting}
          onCancel={cancelAdd}
          onConfirm={confirmOnboard}
        />

        <AddToProspoDialog
          open={!!pendingEvent && !needsOnboarding}
          event={pendingEvent}
          clients={clients}
          submitting={submitting}
          onCancel={cancelAdd}
          onConfirm={confirmAdd}
        />
      </Paper>
    </Box>
  );
};

export default MeetingsPanel;
